import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// P0 FIX: List of ALL auth-related localStorage keys that must be cleared
const AUTH_STORAGE_KEYS = [
  'token',
  'token_stored_at',
  'user',
  'userRoles',
  'shadowSession',
  'auth_state',
  'refresh_token'
];

// P2 FIX: JWT client-side max age (matches server-side 168h / 7 days)
const TOKEN_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// SHADOW MODE — Defence in depth.
// In a shadow tab the bootstrap in `public/index.html` patches
// Storage.prototype so `localStorage.getItem('token')` transparently routes
// to sessionStorage. As a belt-and-braces guard we ALSO read directly from
// sessionStorage whenever the per-tab `is_shadow_tab` flag is set — this way
// AuthContext resolves the correct token even if a future browser quirk
// breaks the prototype patch. The PO tab (no flag) is unaffected.
const isShadowTab = () => {
  try {
    return window.sessionStorage.getItem('is_shadow_tab') === '1';
  } catch (_) {
    return false;
  }
};

const readActiveToken = () => {
  if (isShadowTab()) {
    try {
      return window.sessionStorage.getItem('token');
    } catch (_) { /* fall through */ }
  }
  return localStorage.getItem('token');
};

const readActiveTokenStoredAt = () => {
  if (isShadowTab()) {
    try {
      return window.sessionStorage.getItem('token_stored_at');
    } catch (_) { /* fall through */ }
  }
  return localStorage.getItem('token_stored_at');
};

// Check if the stored token has exceeded its client-side TTL
const isTokenExpiredClientSide = () => {
  const storedAt = readActiveTokenStoredAt();
  if (!storedAt) return true; // No timestamp = treat as expired
  return (Date.now() - parseInt(storedAt, 10)) > TOKEN_MAX_AGE_MS;
};

// P0 FIX: Nuclear clear function - removes ALL auth state from storage
const clearAllAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach(key => {
    localStorage.removeItem(key);
  });
  // Also clear shadow token from sessionStorage (P2 hardening)
  sessionStorage.removeItem('shadow_original_token');
  // FIXED: Only clear auth-related sessionStorage keys, not ALL sessionStorage
  // This preserves booking dates (bookingStartDate, bookingEndDate, bookingVehicleId)
  // and return URLs (postVerificationReturnUrl) during auth transitions
  const sessionKeysToPreserve = [
    'bookingStartDate',
    'bookingEndDate', 
    'bookingVehicleId',
    'postVerificationReturnUrl'
  ];
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (!sessionKeysToPreserve.includes(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null); // P0 FIX: Initialize as null, not from localStorage
  const [legalCompliance, setLegalCompliance] = useState(null);
  const initRef = useRef(false); // Prevent double init

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  api.interceptors.request.use((config) => {
    const storedToken = readActiveToken();
    if (storedToken) {
      // P2 FIX: Check client-side expiry before sending request
      if (isTokenExpiredClientSide()) {
        clearAllAuthStorage();
        setToken(null);
        setUser(null);
        setLegalCompliance(null);
        window.location.href = '/login';
        return Promise.reject(new Error('Token expired'));
      }
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // P0 FIX: Fetch user from backend - ALWAYS get fresh data, never trust cache
  const fetchUser = useCallback(async (forceToken = null) => {
    const tokenToUse = forceToken || localStorage.getItem('token');
    if (!tokenToUse) {
      setUser(null);
      setToken(null);
      setLoading(false);
      return null;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${tokenToUse}` }
      });
      
      const userData = response.data;
      
      // Set state atomically
      setToken(tokenToUse);
      setUser(userData);
      
      // Also fetch legal compliance status
      try {
        const complianceRes = await axios.get(`${API_URL}/api/users/legal-compliance`, {
          headers: { Authorization: `Bearer ${tokenToUse}` }
        });
        setLegalCompliance(complianceRes.data);
      } catch (compErr) {
      }
      
      return userData;
    } catch (error) {
      console.error('[Auth] Failed to fetch user, clearing all auth state:', error);
      // Token is invalid - clear everything
      clearAllAuthStorage();
      setToken(null);
      setUser(null);
      setLegalCompliance(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // P0 FIX: App initialization - clean up stale shadow tokens and validate session
  useEffect(() => {
    if (initRef.current) return; // Prevent double init in StrictMode
    initRef.current = true;
    
    const initAuth = async () => {
      
      // P0 CRITICAL: Check for stale shadow_original_token
      // If shadow_original_token exists but we're NOT in an active shadow session,
      // it means a previous session wasn't cleaned up properly - CLEAR IT
      const shadowToken = sessionStorage.getItem('shadow_original_token');
      const currentToken = readActiveToken();
      
      if (shadowToken && !currentToken) {
        // Shadow token exists but no main token - corrupted state, clear everything
        clearAllAuthStorage();
        setLoading(false);
        return;
      }
      
      if (currentToken) {
        // P2 FIX: Check client-side expiry before hitting the server
        if (isTokenExpiredClientSide()) {
          clearAllAuthStorage();
          setLoading(false);
          return;
        }
        // Validate the current token and fetch user
        await fetchUser(currentToken);
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, [fetchUser]);

  const login = async (email, password) => {
    
    // P0 FIX: NUCLEAR CLEAR - Remove ALL auth state BEFORE doing anything
    clearAllAuthStorage();
    
    // Clear React state immediately
    setUser(null);
    setToken(null);
    setLegalCompliance(null);
    
    // Small delay to ensure state is cleared before proceeding
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Now perform login with clean state
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    
    // Check if MFA is required (second step)
    if (response.data.mfa_required) {
      return { mfa_required: true, mfa_token: response.data.mfa_token, user_id: response.data.user_id };
    }
    
    const { token: newToken, user: userData, legal_compliance } = response.data;
    
    // Set new session atomically
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_stored_at', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    setLegalCompliance(legal_compliance || null);
    
    // P0 FIX: Force fetch from backend to ensure we have the authoritative user data
    const verifiedUser = await fetchUser(newToken);
    
    
    return { user: verifiedUser || userData, legal_compliance };
  };

  const completeMfaLogin = async (mfaToken, code) => {
    const response = await axios.post(`${API_URL}/api/auth/mfa/verify`, { mfa_token: mfaToken, code });
    const { token: newToken, user: userData, legal_compliance } = response.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_stored_at', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    setLegalCompliance(legal_compliance || null);
    
    const verifiedUser = await fetchUser(newToken);
    return { user: verifiedUser || userData, legal_compliance };
  };


  const register = async (data) => {
    
    // P0 FIX: NUCLEAR CLEAR
    clearAllAuthStorage();
    setUser(null);
    setToken(null);
    setLegalCompliance(null);
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { token: newToken, user: userData, legal_compliance } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_stored_at', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    setLegalCompliance(legal_compliance || null);
    
    // Force verify with backend
    const verifiedUser = await fetchUser(newToken);
    
    
    return verifiedUser || userData;
  };

  const processGoogleSession = async (sessionId) => {
    
    // P0 FIX: NUCLEAR CLEAR
    clearAllAuthStorage();
    setUser(null);
    setToken(null);
    setLegalCompliance(null);
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const response = await axios.post(`${API_URL}/api/auth/session`, { session_id: sessionId });
    const { token: newToken, user: userData, legal_compliance, is_new_user } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_stored_at', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    setLegalCompliance(legal_compliance || null);
    
    // Force verify with backend
    const verifiedUser = await fetchUser(newToken);
    
    
    return { user: verifiedUser || userData, is_new_user };
  };

  const logout = () => {
    
    // P0 FIX: NUCLEAR CLEAR - remove everything
    clearAllAuthStorage();
    
    // Clear React state
    setToken(null);
    setUser(null);
    setLegalCompliance(null);
    
  };

  // GUEST CHECKOUT: Silent login with token (no password required)
  // Used when guest checkout creates an account and returns a token
  const loginWithToken = async (newToken, userData = null) => {
    
    // Clear any existing session first
    clearAllAuthStorage();
    setUser(null);
    setToken(null);
    setLegalCompliance(null);
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Set the new token
    localStorage.setItem('token', newToken);
    localStorage.setItem('token_stored_at', Date.now().toString());
    setToken(newToken);
    
    // If user data was provided, set it immediately (optimistic)
    if (userData) {
      setUser(userData);
    }
    
    // Verify with backend to get authoritative user data
    const verifiedUser = await fetchUser(newToken);
    
    
    return verifiedUser || userData;
  };

  // Force clear all session data and reload (for dev/debug or forced logout)
  const clearAllSession = () => {
    clearAllAuthStorage();
    
    setToken(null);
    setUser(null);
    setLegalCompliance(null);
    
    window.location.href = '/';
  };

  const updateUser = (updatedData) => {
    setUser((prev) => ({ ...prev, ...updatedData }));
  };

  const acceptLegalTerms = async () => {
    try {
      const response = await api.post('/users/accept-legal-terms');
      // Refresh compliance status
      const complianceRes = await api.get('/users/legal-compliance');
      setLegalCompliance(complianceRes.data);
      return response.data;
    } catch (error) {
      console.error('Failed to accept legal terms:', error);
      throw error;
    }
  };

  const refreshLegalCompliance = async () => {
    try {
      const response = await api.get('/users/legal-compliance');
      setLegalCompliance(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh legal compliance:', error);
    }
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  // P0 CRITICAL: Check if user is platform owner (admin)
  // Platform owner CANNOT be a host, CANNOT book vehicles
  const isPlatformOwner = () => {
    return user?.is_platform_owner === true || hasRole('platform_owner');
  };
  
  // P3 CRITICAL: Check if user is a platform operator (admin/super_admin)
  // Platform operators are EXEMPT from host/renter legal acceptance flows
  // They are operators of the platform, not booking participants
  const isPlatformOperator = () => {
    return hasRole('super_admin') || hasRole('admin') || isPlatformOwner();
  };

  // P0 CRITICAL: isHost checks host_status === "active" (canonical source)
  // Platform owner can NEVER be a host
  const isHost = () => {
    // Platform owner can NEVER be a host
    if (isPlatformOwner()) return false;
    // Check canonical host_status field
    if (user?.host_status === "active") return true;
    // Backward compat
    if (user?.is_host === true) return true;
    return false;
  };
  
  // isActiveHost is the strict check for host_status
  const isActiveHost = () => {
    if (isPlatformOwner()) return false;
    return user?.host_status === "active";
  };
  
  // P0 CRITICAL: Can user book vehicles? (customer capability)
  // Platform owner CANNOT book vehicles
  const canBook = () => {
    return !isPlatformOwner();
  };
  
  const isOwner = () => isHost(); // Deprecated alias for isHost
  const isAdmin = () => hasRole('admin');
  const isSuperAdmin = () => hasRole('super_admin');
  
  // P3 ROLE-BASED ACCESS: Determine admin role level
  // CANONICAL STRUCTURE:
  // - super_admin role → Super Admin access (superadmin@hirehubaustralia.com)
  // - admin role → Admin access (admin@hirehubaustralia.com, hirehubaustralia@gmail.com)
  // Returns: 'super_admin' | 'admin' | null
  const getAdminRole = () => {
    // Only super_admin role grants super admin level
    if (hasRole('super_admin')) return 'super_admin';
    // Admin role grants admin level (even with is_platform_owner flag)
    if (hasRole('admin')) return 'admin';
    // Platform owner flag alone does NOT grant admin access anymore
    return null;
  };
  
  // P3: Check if user has at least admin-level access
  const hasAdminAccess = () => {
    return hasRole('super_admin') || hasRole('admin');
  };
  
  const isVerified = () => user?.verification_status === 'verified';
  const isLegalCompliant = () => legalCompliance?.is_compliant === true;
  const needsLegalReacceptance = () => legalCompliance && !legalCompliance.is_compliant && legalCompliance.needs_reacceptance?.length > 0;

  // Feature entitlements — safe defaults for users without stored fields
  const hasFeature = (featureName) => {
    return user?.feature_flags?.[featureName] === true;
  };
  const getSubscriptionTier = () => user?.subscription_tier || 'free';
  
  // P0 SECURITY: Check if user needs to change their password (ONLY for local accounts)
  const requiresPasswordReset = () => {
    // Never require password reset for OAuth users (google, apple, etc.)
    if (user?.auth_provider && user.auth_provider !== 'local') {
      return false;
    }
    return user?.password_reset_required === true;
  };

  const value = {
    user,
    token,
    loading,
    login,
    completeMfaLogin,
    loginWithToken, // GUEST CHECKOUT: Silent login with token
    register,
    logout,
    updateUser,
    hasRole,
    isHost,
    isActiveHost,
    canBook,
    isOwner, // Deprecated alias for isHost
    isAdmin,
    isSuperAdmin,
    isPlatformOwner,
    isPlatformOperator, // P3: Admin/SuperAdmin legal exemption check
    getAdminRole,
    hasAdminAccess,
    isVerified,
    isLegalCompliant,
    needsLegalReacceptance,
    hasFeature,
    getSubscriptionTier,
    requiresPasswordReset,
    legalCompliance,
    acceptLegalTerms,
    refreshLegalCompliance,
    processGoogleSession,
    refreshUser: fetchUser,
    clearAllSession,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
